import { useEffect, useMemo, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import {
  ROTULO_DA_CATEGORIA,
  lerOpcoes,
  previaDaRegua,
  quantosNiveis,
  reais,
  salvarMeta,
  simular,
  valorDoIndicador,
} from '../../metas';
import type {
  BaseDaMeta,
  IndicadorDoCatalogo,
  MetaGravada,
  OpcoesDasMetas,
  Previa,
  RegraDaMeta,
  Requisito,
  Simulacao,
} from '../../metas';
import { useEscape } from '../equipe/Equipe';

/**
 * O ASSISTENTE DE META (dono, 29/09 — itens 19–35 do Super Bloco e 1–28 do Complemento). Tudo o
 * que a meta faz sai daqui, persistido; o motor só interpreta. A régua e a simulação vêm do
 * SERVIDOR (esta tela não calcula dinheiro). Mudar meta ATIVA pede "vale no mês que vem" ou
 * "vale já" (com confirmação) — nunca muda calado.
 */

type Passo = 'basico' | 'indicador' | 'regua' | 'premio' | 'regras' | 'salvar';
const PASSOS: readonly [Passo, string][] = [
  ['basico', 'Dados básicos'],
  ['indicador', 'Indicador'],
  ['regua', 'Base e níveis'],
  ['premio', 'Premiação'],
  ['regras', 'Requisitos e público'],
  ['salvar', 'Simular e salvar'],
];

const hoje = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const dec = (s: string) => s.trim().replace(',', '.');
const repetir = (v: string, n: number) => Array.from({ length: n }, () => v);

function regraInicial(ind: IndicadorDoCatalogo | undefined): RegraDaMeta {
  return {
    filtro: null,
    direcao: ind?.direcaoSugerida ?? 'MAIOR_MELHOR',
    base:
      ind?.codigo === 'margem_pct' || ind?.codigo === 'lucro_rs'
        ? { tipo: 'INDIVIDUAL', meses: 1 }
        : { tipo: 'INDIVIDUAL', meses: 12 },
    niveis: { tipo: 'COMPOSTO', passosPct: repetir('5', 5) },
    premiacao: {
      ativa: false,
      premio: { tipo: 'NENHUM' },
      acumulo: 'MAIOR_NIVEL',
      tetoDaMeta: null,
    },
    requisitos: { modo: 'TODOS', itens: [] },
    amostraMinima: ind?.amostra === 'cupons' ? 30 : 0,
    toleranciaDeRiscoPct: '0.5',
    publico: { tipo: 'TODOS', contas: [], excecoes: [] },
    personalizadas: {},
    inicio: hoje(),
    fim: null,
    peso: 1,
  };
}

export function EditorDeMeta({
  sessao,
  catalogo,
  meta,
  duplicar,
  podeDinheiro,
  aoFechar,
  aoSalvar,
}: {
  sessao: Sessao;
  catalogo: IndicadorDoCatalogo[];
  meta: MetaGravada | null;
  duplicar: boolean;
  podeDinheiro: boolean;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  useEscape(aoFechar, true);
  const editando = meta !== null && !duplicar;
  const regraDaMeta = meta?.agendada?.regra ?? meta?.regra ?? null;
  const [passo, setPasso] = useState<Passo>(meta ? 'regua' : 'basico');
  const [nome, setNome] = useState(meta ? (duplicar ? `${meta.nome} (cópia)` : meta.nome) : '');
  const [descricao, setDescricao] = useState(meta?.descricao ?? '');
  const [tipo, setTipo] = useState<'PERMANENTE' | 'CAMPANHA'>(meta?.tipo ?? 'PERMANENTE');
  const [indicador, setIndicador] = useState(meta?.indicador ?? catalogo[0]?.codigo ?? '');
  const ind = catalogo.find((i) => i.codigo === indicador);
  const [regra, setRegra] = useState<RegraDaMeta>(regraDaMeta ?? regraInicial(ind));
  const [opcoes, setOpcoes] = useState<OpcoesDasMetas | null>(null);
  const [baseExemplo, setBaseExemplo] = useState('100');
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [motivo, setMotivo] = useState('');
  const [aplicar, setAplicar] = useState<'PROXIMA' | 'AGORA'>('PROXIMA');
  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [sim, setSim] = useState<Simulacao | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [simConta, setSimConta] = useState('');

  useEffect(() => {
    void lerOpcoes().then((r) => r.ok && setOpcoes(r.dados));
  }, []);

  const mudar = (p: Partial<RegraDaMeta>) => setRegra((r) => ({ ...r, ...p }));
  const n = quantosNiveis(regra.niveis);

  // Prévia da régua — do servidor, com um respiro para não chamar a cada tecla.
  const pedido = useRef(0);
  useEffect(() => {
    const meu = ++pedido.current;
    const t = setTimeout(() => {
      void previaDaRegua(
        sessao,
        indicador,
        regra,
        regra.base.tipo === 'FIXA' ? null : dec(baseExemplo) || null,
      ).then((r) => {
        if (meu === pedido.current && r.tipo === 'confirmada') setPrevia(r.dados);
      });
    }, 350);
    return () => clearTimeout(t);
  }, [sessao, indicador, regra, baseExemplo]);

  const trocarIndicador = (codigo: string) => {
    setIndicador(codigo);
    const novo = catalogo.find((i) => i.codigo === codigo);
    setRegra((r) => ({
      ...r,
      direcao: novo?.direcaoSugerida ?? r.direcao,
      filtro: novo?.aceitaFiltro ? r.filtro : null,
      amostraMinima: novo?.amostra === 'cupons' ? Math.max(r.amostraMinima, 30) : r.amostraMinima,
    }));
  };

  const problemas = previa?.problemas ?? [];
  const pagaSemPermissao = regra.premiacao.ativa && !podeDinheiro;
  const salvar = (status: 'RASCUNHO' | 'ATIVA') => {
    setEnviando(true);
    setFalha(null);
    void salvarMeta(sessao, {
      ...(editando ? { id: meta.id, versaoEsperada: meta.versao } : {}),
      nome,
      descricao: descricao || null,
      indicador,
      tipo,
      status,
      regra,
      ...(editando && meta.status === 'ATIVA'
        ? { aplicar, confirmarAgora: aplicar === 'AGORA' && confirmar }
        : {}),
      motivo,
    }).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoSalvar();
      else
        setFalha(
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Sem resposta do servidor — confira na lista antes de repetir.',
        );
    });
  };
  const rodarSimulacao = () => {
    setSimulando(true);
    setSim(null);
    void simular(sessao, { indicador, nome, regra, conta: simConta || null }).then((r) => {
      setSimulando(false);
      if (r.tipo === 'confirmada') setSim(r.dados);
      else
        setFalha(r.tipo === 'recusada' ? r.mensagem : 'A simulação não respondeu — tente de novo.');
    });
  };

  return (
    <div className="ac-veu" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <aside
        className="ac-drawer mp-drawer mp-drawer-largo"
        role="dialog"
        aria-modal="true"
        aria-label="Meta"
      >
        <div className="ac-drawer-cabeca">
          <div>
            <div className="ac-drawer-nome">
              {editando ? `Editar · ${meta.nome}` : duplicar ? 'Duplicar meta' : 'Nova meta'}
            </div>
            <div className="ac-drawer-meta">
              {ind?.rotulo ?? 'escolha um indicador'} ·{' '}
              {regra.premiacao.ativa
                ? `paga até ${reais(previa?.premioMaximo)}`
                : 'só acompanhamento'}
            </div>
          </div>
          <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={15} traco={1.7} />
          </button>
        </div>

        <nav className="mp-passos" aria-label="Etapas">
          {PASSOS.map(([k, rotulo], i) => (
            <button
              key={k}
              type="button"
              className={`mp-passo${passo === k ? ' mp-passo-atual' : ''}`}
              onClick={() => setPasso(k)}
            >
              <i>{i + 1}</i>
              {rotulo}
            </button>
          ))}
        </nav>

        <div className="mp-editor">
          {passo === 'basico' && (
            <section className="mp-sec">
              <label className="mp-campo">
                <span>Nome da meta</span>
                <input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="ex.: Melhoria do Ticket Médio"
                  autoFocus
                />
              </label>
              <label className="mp-campo">
                <span>Descrição (o balconista lê)</span>
                <input
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="ex.: Premiar aumento do Ticket Médio individual."
                />
              </label>
              <div className="mp-opcoes">
                <Opcao
                  marcado={tipo === 'PERMANENTE'}
                  aoMarcar={() => {
                    setTipo('PERMANENTE');
                    mudar({ fim: null });
                  }}
                  titulo="Permanente"
                  texto="Vale todo mês, sem data para acabar."
                />
                <Opcao
                  marcado={tipo === 'CAMPANHA'}
                  aoMarcar={() => setTipo('CAMPANHA')}
                  titulo="Campanha"
                  texto="Tem início e fim — encerra sozinha."
                />
              </div>
              <div className="mp-dupla">
                <label className="mp-campo">
                  <span>Começa em</span>
                  <input
                    type="date"
                    value={regra.inicio}
                    onChange={(e) => mudar({ inicio: e.target.value })}
                  />
                </label>
                <label className="mp-campo">
                  <span>{tipo === 'CAMPANHA' ? 'Termina em' : 'Termina em (opcional)'}</span>
                  <input
                    type="date"
                    value={regra.fim ?? ''}
                    onChange={(e) => mudar({ fim: e.target.value || null })}
                  />
                </label>
              </div>
            </section>
          )}

          {passo === 'indicador' && (
            <section className="mp-sec">
              <p className="ac-nota">
                O indicador é só a métrica. Escolher um aqui não faz ninguém ganhar nada — isso é na
                Premiação.
              </p>
              {[...new Set(catalogo.map((i) => i.categoria))].map((cat) => (
                <div key={cat} className="mp-catalogo">
                  <span className="mp-rotulo">{ROTULO_DA_CATEGORIA[cat]}</span>
                  <div className="mp-catalogo-itens">
                    {catalogo
                      .filter((i) => i.categoria === cat)
                      .map((i) => (
                        <button
                          key={i.codigo}
                          type="button"
                          className={`mp-ind-cartao${indicador === i.codigo ? ' mp-ind-cartao-sel' : ''}`}
                          onClick={() => trocarIndicador(i.codigo)}
                        >
                          <b>{i.rotulo}</b>
                          <span>
                            {i.direcaoSugerida === 'MENOR_MELHOR'
                              ? '↓ menor é melhor'
                              : '↑ maior é melhor'}
                            {i.natureza === 'SOMA' ? ' · soma no período' : ' · média'}
                          </span>
                          {i.observacao && <em>{i.observacao}</em>}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
              {ind?.aceitaFiltro && (
                <FiltroDeProduto
                  opcoes={opcoes}
                  filtro={regra.filtro}
                  aoMudar={(f) => mudar({ filtro: f })}
                />
              )}
            </section>
          )}

          {passo === 'regua' && (
            <section className="mp-sec">
              <span className="mp-rotulo">O que significa melhorar?</span>
              <div className="mp-opcoes">
                <Opcao
                  marcado={regra.direcao === 'MAIOR_MELHOR'}
                  aoMarcar={() => mudar({ direcao: 'MAIOR_MELHOR' })}
                  titulo="↑ Maior é melhor"
                  texto="Ticket, margem, vendas, cupons…"
                />
                <Opcao
                  marcado={regra.direcao === 'MENOR_MELHOR'}
                  aoMarcar={() => mudar({ direcao: 'MENOR_MELHOR' })}
                  titulo="↓ Menor é melhor"
                  texto="Desconto, cancelamentos, devoluções…"
                />
              </div>
              <Base
                base={regra.base}
                aoMudar={(b) => mudar({ base: b })}
                observacao={ind?.observacao}
              />
              <Niveis regra={regra} aoMudar={(niveis) => mudar({ niveis })} />
              <Regua
                previa={previa}
                regra={regra}
                ind={ind}
                baseExemplo={baseExemplo}
                aoBase={setBaseExemplo}
              />
            </section>
          )}

          {passo === 'premio' && (
            <section className="mp-sec">
              <label className="mp-interruptor mp-interruptor-grande">
                <input
                  type="checkbox"
                  checked={regra.premiacao.ativa}
                  disabled={!podeDinheiro}
                  onChange={(e) =>
                    mudar({
                      premiacao: e.target.checked
                        ? {
                            ...regra.premiacao,
                            ativa: true,
                            premio:
                              regra.premiacao.premio.tipo === 'NENHUM'
                                ? {
                                    tipo: 'POR_NIVEL',
                                    valores: Array.from({ length: n }, (_, k) =>
                                      String((k + 1) * 10),
                                    ),
                                  }
                                : regra.premiacao.premio,
                          }
                        : { ...regra.premiacao, ativa: false },
                    })
                  }
                />
                <span>
                  <b>Usar este indicador para premiação</b>
                  <em>
                    Desligado, a meta aparece no Meu Desempenho mas não mexe no bônus, não gera
                    saldo e não entra no teto.
                  </em>
                </span>
              </label>
              {!podeDinheiro && (
                <p className="ac-nota">
                  Ligar ou mudar premiação é de quem cuida do dinheiro das metas (o dono).
                </p>
              )}
              {regra.premiacao.ativa && (
                <Premio regra={regra} n={n} aoMudar={(p) => mudar({ premiacao: p })} />
              )}
            </section>
          )}

          {passo === 'regras' && (
            <section className="mp-sec">
              <div className="mp-dupla">
                <label className="mp-campo">
                  <span>
                    Amostra mínima (
                    {ind?.amostra === 'cupons'
                      ? 'cupons'
                      : ind?.amostra === 'entregas'
                        ? 'entregas'
                        : ind?.amostra === 'lancamentos'
                          ? 'lançamentos'
                          : 'dias trabalhados'}
                    )
                  </span>
                  <input
                    inputMode="numeric"
                    value={String(regra.amostraMinima)}
                    onChange={(e) =>
                      mudar({ amostraMinima: Number(e.target.value.replace(/\D/g, '') || 0) })
                    }
                  />
                  <em>
                    Abaixo disso o resultado é parcial e não vale dinheiro (uma venda de R$ 600 não
                    faz campeão).
                  </em>
                </label>
                <label className="mp-campo">
                  <span>Nível em risco quando a folga for até (%)</span>
                  <input
                    inputMode="decimal"
                    value={regra.toleranciaDeRiscoPct}
                    onChange={(e) => mudar({ toleranciaDeRiscoPct: dec(e.target.value) })}
                  />
                  <em>Evita alerta a cada oscilação pequena.</em>
                </label>
              </div>
              <Requisitos
                catalogo={catalogo}
                regra={regra}
                aoMudar={(requisitos) => mudar({ requisitos })}
                podeDinheiro={podeDinheiro}
              />
              <Publico opcoes={opcoes} regra={regra} aoMudar={mudar} />
              <label className="mp-campo mp-campo-curto">
                <span>Peso (ranking futuro — não mexe em dinheiro)</span>
                <input
                  inputMode="decimal"
                  value={String(regra.peso)}
                  onChange={(e) => mudar({ peso: Number(dec(e.target.value)) || 0 })}
                />
              </label>
            </section>
          )}

          {passo === 'salvar' && (
            <section className="mp-sec">
              <div className="mp-simulacao">
                <div className="mp-simulacao-cabeca">
                  <div>
                    <b>Se esta meta estivesse ativa no mês passado</b>
                    <em>Use antes de ativar: evita meta que explode o custo.</em>
                  </div>
                  <select
                    value={simConta}
                    onChange={(e) => setSimConta(e.target.value)}
                    aria-label="Funcionário"
                  >
                    <option value="">Todos os balconistas</option>
                    {opcoes?.pessoas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ac-botao"
                    disabled={simulando || problemas.length > 0}
                    onClick={rodarSimulacao}
                  >
                    <Icone nome="gauge" tamanho={14} traco={1.8} />{' '}
                    {simulando ? 'Simulando…' : 'Simular'}
                  </button>
                </div>
                {sim && <ResultadoDaSimulacao sim={sim} ind={ind} />}
              </div>

              {problemas.length > 0 && (
                <div className="mp-problemas" role="alert">
                  <b>Antes de salvar:</b>
                  <ul>
                    {problemas.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pagaSemPermissao && (
                <p className="mp-falha">
                  Esta meta paga bônus: só quem cuida do dinheiro das metas pode salvá-la.
                </p>
              )}

              {editando && meta.status === 'ATIVA' && (
                <div className="mp-aplicar">
                  <b>Esta alteração afetará uma competência em andamento.</b>
                  <div className="mp-opcoes">
                    <Opcao
                      marcado={aplicar === 'PROXIMA'}
                      aoMarcar={() => setAplicar('PROXIMA')}
                      titulo="Aplicar no mês que vem"
                      texto="Recomendado. Este mês continua com a regra de hoje."
                    />
                    <Opcao
                      marcado={aplicar === 'AGORA'}
                      aoMarcar={() => setAplicar('AGORA')}
                      titulo="Aplicar agora"
                      texto="Muda o bônus projetado deste mês de todos. Fica na trilha de auditoria."
                    />
                  </div>
                  {aplicar === 'AGORA' && (
                    <label className="mp-interruptor">
                      <input
                        type="checkbox"
                        checked={confirmar}
                        onChange={(e) => setConfirmar(e.target.checked)}
                      />
                      <span>Confirmo que quero mudar o mês em andamento.</span>
                    </label>
                  )}
                </div>
              )}

              <label className="mp-campo">
                <span>
                  Por que está {editando ? 'mudando' : 'criando'}? (vai para a trilha de auditoria)
                </span>
                <input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="ex.: meta de ticket de outubro combinada com o Carlos"
                />
              </label>
              {falha && (
                <p className="mp-falha" role="alert">
                  {falha}
                </p>
              )}
              <div className="mp-salvar">
                {editando ? (
                  <button
                    type="button"
                    className="ac-botao ac-botao-primario"
                    disabled={
                      enviando ||
                      problemas.length > 0 ||
                      motivo.trim().length < 5 ||
                      pagaSemPermissao ||
                      (aplicar === 'AGORA' && meta.status === 'ATIVA' && !confirmar)
                    }
                    onClick={() => salvar('RASCUNHO')}
                  >
                    {enviando ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ac-botao"
                      disabled={
                        enviando ||
                        problemas.length > 0 ||
                        motivo.trim().length < 5 ||
                        pagaSemPermissao ||
                        nome.trim().length < 2
                      }
                      onClick={() => salvar('RASCUNHO')}
                    >
                      Salvar rascunho
                    </button>
                    <button
                      type="button"
                      className="ac-botao ac-botao-primario"
                      disabled={
                        enviando ||
                        problemas.length > 0 ||
                        motivo.trim().length < 5 ||
                        pagaSemPermissao ||
                        nome.trim().length < 2
                      }
                      onClick={() => salvar('ATIVA')}
                    >
                      {enviando ? 'Salvando…' : 'Salvar e ativar'}
                    </button>
                  </>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="mp-rodape">
          <button
            type="button"
            className="ac-botao"
            disabled={passo === 'basico'}
            onClick={() =>
              setPasso(
                PASSOS[Math.max(0, PASSOS.findIndex(([k]) => k === passo) - 1)]?.[0] ?? 'basico',
              )
            }
          >
            Voltar
          </button>
          {passo !== 'salvar' && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() =>
                setPasso(PASSOS[PASSOS.findIndex(([k]) => k === passo) + 1]?.[0] ?? 'salvar')
              }
            >
              Continuar
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function Opcao({
  marcado,
  aoMarcar,
  titulo,
  texto,
}: {
  marcado: boolean;
  aoMarcar: () => void;
  titulo: string;
  texto: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={marcado}
      className={`mp-opcao${marcado ? ' mp-opcao-sel' : ''}`}
      onClick={aoMarcar}
    >
      <b>{titulo}</b>
      <span>{texto}</span>
    </button>
  );
}

function Base({
  base,
  aoMudar,
  observacao,
}: {
  base: BaseDaMeta;
  aoMudar: (b: BaseDaMeta) => void;
  observacao?: string | undefined;
}) {
  const meses = base.tipo === 'INDIVIDUAL' || base.tipo === 'LOJA' ? base.meses : 12;
  return (
    <div className="mp-bloco">
      <span className="mp-rotulo">Referência (base) — meses fechados antes do mês da meta</span>
      <div className="mp-linha">
        <select
          value={base.tipo}
          onChange={(e) => {
            const t = e.target.value as BaseDaMeta['tipo'];
            aoMudar(
              t === 'FIXA'
                ? { tipo: 'FIXA', valor: '100' }
                : t === 'SEM_BASE'
                  ? { tipo: 'SEM_BASE' }
                  : { tipo: t, meses },
            );
          }}
        >
          <option value="INDIVIDUAL">Média individual</option>
          <option value="LOJA">Média da loja</option>
          <option value="FIXA">Valor fixo</option>
          <option value="SEM_BASE">Sem base (valores manuais)</option>
        </select>
        {(base.tipo === 'INDIVIDUAL' || base.tipo === 'LOJA') && (
          <select
            value={String(base.meses)}
            onChange={(e) =>
              aoMudar({ tipo: base.tipo, meses: Number(e.target.value) as 1 | 3 | 6 | 12 })
            }
          >
            <option value="1">mês anterior</option>
            <option value="3">últimos 3 meses</option>
            <option value="6">últimos 6 meses</option>
            <option value="12">últimos 12 meses</option>
          </select>
        )}
        {base.tipo === 'FIXA' && (
          <input
            inputMode="decimal"
            value={base.valor}
            onChange={(e) => aoMudar({ tipo: 'FIXA', valor: dec(e.target.value) })}
            aria-label="Base fixa"
          />
        )}
      </div>
      {observacao && <p className="ac-nota">{observacao}</p>}
    </div>
  );
}

function Niveis({
  regra,
  aoMudar,
}: {
  regra: RegraDaMeta;
  aoMudar: (n: RegraDaMeta['niveis']) => void;
}) {
  const r = regra.niveis;
  const qtd = quantosNiveis(r);
  const porcentos = r.tipo === 'COMPOSTO' || r.tipo === 'SOBRE_BASE' ? r.passosPct : null;
  const iguais = porcentos ? porcentos.every((p) => p === porcentos[0]) : true;
  const [diferentes, setDiferentes] = useState(!iguais);
  const mudarQtd = (q: number) => {
    const k = Math.max(1, Math.min(20, q));
    if (r.tipo === 'MANUAL')
      aoMudar({
        tipo: 'MANUAL',
        alvos: Array.from(
          { length: k },
          (_, i) => r.alvos[i] ?? r.alvos[r.alvos.length - 1] ?? '0',
        ),
      });
    else if (r.tipo === 'VALOR_FIXO') aoMudar({ ...r, quantidade: k });
    else
      aoMudar({
        ...r,
        passosPct: Array.from({ length: k }, (_, i) => r.passosPct[i] ?? r.passosPct[0] ?? '5'),
      });
  };
  return (
    <div className="mp-bloco">
      <span className="mp-rotulo">Como a régua sobe</span>
      <div className="mp-linha">
        <select
          value={r.tipo}
          onChange={(e) => {
            const t = e.target.value as RegraDaMeta['niveis']['tipo'];
            aoMudar(
              t === 'MANUAL'
                ? {
                    tipo: 'MANUAL',
                    alvos: Array.from({ length: qtd }, (_, i) => String((i + 1) * 10)),
                  }
                : t === 'VALOR_FIXO'
                  ? { tipo: 'VALOR_FIXO', passo: '1', quantidade: qtd }
                  : { tipo: t, passosPct: porcentos ?? repetir('5', qtd) },
            );
          }}
        >
          <option value="COMPOSTO">% composto a cada nível</option>
          <option value="SOBRE_BASE">% acumulado sobre a base</option>
          <option value="VALOR_FIXO">Valor fixo por nível</option>
          <option value="MANUAL">Valores manuais</option>
        </select>
        <label className="mp-campo mp-campo-mini">
          <span>Níveis</span>
          <input
            inputMode="numeric"
            value={String(qtd)}
            onChange={(e) => mudarQtd(Number(e.target.value.replace(/\D/g, '') || 1))}
          />
        </label>
        {porcentos && !diferentes && (
          <label className="mp-campo mp-campo-mini">
            <span>Incremento (%)</span>
            <input
              inputMode="decimal"
              value={porcentos[0] ?? '5'}
              onChange={(e) =>
                aoMudar({
                  ...(r as { tipo: 'COMPOSTO' | 'SOBRE_BASE'; passosPct: string[] }),
                  passosPct: repetir(dec(e.target.value), qtd),
                })
              }
            />
          </label>
        )}
        {r.tipo === 'VALOR_FIXO' && (
          <label className="mp-campo mp-campo-mini">
            <span>Por nível</span>
            <input
              inputMode="decimal"
              value={r.passo}
              onChange={(e) => aoMudar({ ...r, passo: dec(e.target.value) })}
            />
          </label>
        )}
      </div>
      {porcentos && (
        <label className="mp-interruptor">
          <input
            type="checkbox"
            checked={diferentes}
            onChange={(e) => setDiferentes(e.target.checked)}
          />
          <span>Percentual diferente em cada nível</span>
        </label>
      )}
      {porcentos && diferentes && (
        <div className="mp-grade-niveis">
          {porcentos.map((p, i) => (
            <label key={i} className="mp-campo mp-campo-mini">
              <span>N{i + 1} (%)</span>
              <input
                inputMode="decimal"
                value={p}
                onChange={(e) => {
                  const v = [...porcentos];
                  v[i] = dec(e.target.value);
                  aoMudar({
                    ...(r as { tipo: 'COMPOSTO' | 'SOBRE_BASE'; passosPct: string[] }),
                    passosPct: v,
                  });
                }}
              />
            </label>
          ))}
        </div>
      )}
      {r.tipo === 'MANUAL' && (
        <div className="mp-grade-niveis">
          {r.alvos.map((a, i) => (
            <label key={i} className="mp-campo mp-campo-mini">
              <span>Alvo N{i + 1}</span>
              <input
                inputMode="decimal"
                value={a}
                onChange={(e) => {
                  const v = [...r.alvos];
                  v[i] = dec(e.target.value);
                  aoMudar({ tipo: 'MANUAL', alvos: v });
                }}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** A régua como o balconista vai ver — os números vêm do servidor. */
function Regua({
  previa,
  regra,
  ind,
  baseExemplo,
  aoBase,
}: {
  previa: Previa | null;
  regra: RegraDaMeta;
  ind: IndicadorDoCatalogo | undefined;
  baseExemplo: string;
  aoBase: (v: string) => void;
}) {
  const usaExemplo = regra.base.tipo === 'INDIVIDUAL' || regra.base.tipo === 'LOJA';
  const u = ind?.unidade ?? 'RS';
  const c = ind?.casas ?? 2;
  return (
    <div className="mp-regua-previa">
      <div className="mp-linha">
        <b>Régua</b>
        {usaExemplo && (
          <label className="mp-campo mp-campo-mini">
            <span>Base de exemplo</span>
            <input
              inputMode="decimal"
              value={baseExemplo}
              onChange={(e) => aoBase(e.target.value)}
            />
          </label>
        )}
        {usaExemplo && <em>A base real é de cada balconista — aqui é só para ver a régua.</em>}
      </div>
      {!previa?.alvos ? (
        <p className="ac-nota">{previa?.problemas[0] ?? 'Calculando a régua…'}</p>
      ) : (
        <ol className="mp-regua">
          <li className="mp-regua-base">
            <span>Base</span>
            <b>
              {regra.base.tipo === 'SEM_BASE'
                ? '—'
                : valorDoIndicador(
                    regra.base.tipo === 'FIXA' ? regra.base.valor : dec(baseExemplo),
                    u,
                    c,
                  )}
            </b>
          </li>
          {previa.alvos.map((a, i) => (
            <li key={i}>
              <span>{regra.nomesDosNiveis?.[i] || `N${i + 1}`}</span>
              <b>{valorDoIndicador(a, u, c)}</b>
              {regra.premiacao.ativa && <em>{reais(previa.premios[i])}</em>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Premio({
  regra,
  n,
  aoMudar,
}: {
  regra: RegraDaMeta;
  n: number;
  aoMudar: (p: RegraDaMeta['premiacao']) => void;
}) {
  const p = regra.premiacao;
  const [modo, setModo] = useState<'DEGRAU' | 'POR_NIVEL' | 'UNICO'>(
    p.premio.tipo === 'UNICO' ? 'UNICO' : 'POR_NIVEL',
  );
  const valores =
    p.premio.tipo === 'POR_NIVEL' ? p.premio.valores : Array.from({ length: n }, () => '0');
  const [degrau, setDegrau] = useState('10');
  const ajustar = (vs: string[]) =>
    Array.from({ length: n }, (_, i) => vs[i] ?? vs[vs.length - 1] ?? '0');
  return (
    <div className="mp-bloco">
      <span className="mp-rotulo">Como o valor é pago</span>
      <div className="mp-opcoes">
        <Opcao
          marcado={modo === 'DEGRAU'}
          aoMarcar={() => {
            setModo('DEGRAU');
            aoMudar({
              ...p,
              premio: {
                tipo: 'POR_NIVEL',
                valores: Array.from({ length: n }, (_, k) => String((k + 1) * Number(degrau))),
              },
            });
          }}
          titulo="Valor fixo por degrau"
          texto="Cada nível vale +R$ X (10, 20, 30…)."
        />
        <Opcao
          marcado={modo === 'POR_NIVEL'}
          aoMarcar={() => {
            setModo('POR_NIVEL');
            aoMudar({ ...p, premio: { tipo: 'POR_NIVEL', valores: ajustar(valores) } });
          }}
          titulo="Valor em cada nível"
          texto="Você digita o valor de cada nível."
        />
        <Opcao
          marcado={modo === 'UNICO'}
          aoMarcar={() => {
            setModo('UNICO');
            aoMudar({ ...p, premio: { tipo: 'UNICO', valor: '100' } });
          }}
          titulo="Valor único"
          texto="Paga uma vez ao bater o último nível."
        />
      </div>
      {modo === 'DEGRAU' && (
        <label className="mp-campo mp-campo-mini">
          <span>Cada nível vale + (R$)</span>
          <input
            inputMode="decimal"
            value={degrau}
            onChange={(e) => {
              const v = dec(e.target.value);
              setDegrau(v);
              aoMudar({
                ...p,
                premio: {
                  tipo: 'POR_NIVEL',
                  valores: Array.from({ length: n }, (_, k) => String((k + 1) * (Number(v) || 0))),
                },
              });
            }}
          />
        </label>
      )}
      {modo === 'POR_NIVEL' && (
        <div className="mp-grade-niveis">
          {ajustar(valores).map((v, i) => (
            <label key={i} className="mp-campo mp-campo-mini">
              <span>N{i + 1} (R$)</span>
              <input
                inputMode="decimal"
                value={v}
                onChange={(e) => {
                  const vs = ajustar(valores);
                  vs[i] = dec(e.target.value);
                  aoMudar({ ...p, premio: { tipo: 'POR_NIVEL', valores: vs } });
                }}
              />
            </label>
          ))}
        </div>
      )}
      {modo === 'UNICO' && p.premio.tipo === 'UNICO' && (
        <label className="mp-campo mp-campo-mini">
          <span>Valor (R$)</span>
          <input
            inputMode="decimal"
            value={p.premio.valor}
            onChange={(e) =>
              aoMudar({ ...p, premio: { tipo: 'UNICO', valor: dec(e.target.value) } })
            }
          />
        </label>
      )}
      {modo !== 'UNICO' && (
        <>
          <span className="mp-rotulo">Forma de pagamento dos níveis</span>
          <div className="mp-opcoes">
            <Opcao
              marcado={p.acumulo === 'MAIOR_NIVEL'}
              aoMarcar={() => aoMudar({ ...p, acumulo: 'MAIOR_NIVEL' })}
              titulo="Maior nível define o valor"
              texto="Padrão. No N3 recebe o valor do N3."
            />
            <Opcao
              marcado={p.acumulo === 'SOMA'}
              aoMarcar={() => aoMudar({ ...p, acumulo: 'SOMA' })}
              titulo="Somar os níveis conquistados"
              texto="No N3 recebe N1 + N2 + N3."
            />
          </div>
        </>
      )}
      <label className="mp-interruptor">
        <input
          type="checkbox"
          checked={p.tetoDaMeta !== null}
          onChange={(e) => aoMudar({ ...p, tetoDaMeta: e.target.checked ? '50' : null })}
        />
        <span>Limite máximo desta meta</span>
      </label>
      {p.tetoDaMeta !== null && (
        <label className="mp-campo mp-campo-mini">
          <span>Teto (R$)</span>
          <input
            inputMode="decimal"
            value={p.tetoDaMeta}
            onChange={(e) => aoMudar({ ...p, tetoDaMeta: dec(e.target.value) })}
          />
        </label>
      )}
    </div>
  );
}

function FiltroDeProduto({
  opcoes,
  filtro,
  aoMudar,
}: {
  opcoes: OpcoesDasMetas | null;
  filtro: RegraDaMeta['filtro'];
  aoMudar: (f: RegraDaMeta['filtro']) => void;
}) {
  const [busca, setBusca] = useState('');
  const classes = useMemo(
    () => [...new Set((opcoes?.grupos ?? []).map((g) => g.classificacao))].sort(),
    [opcoes],
  );
  const grupos = useMemo(
    () => [...new Set((opcoes?.grupos ?? []).map((g) => g.grupo))].sort(),
    [opcoes],
  );
  const sel = { grupos: filtro?.grupos ?? [], classificacoes: filtro?.classificacoes ?? [] };
  const alternar = (campo: 'grupos' | 'classificacoes', v: string) => {
    const atual = new Set(sel[campo]);
    if (atual.has(v)) atual.delete(v);
    else atual.add(v);
    const novo = { ...sel, [campo]: [...atual] };
    aoMudar(novo.grupos.length || novo.classificacoes.length ? novo : null);
  };
  const q = busca.trim().toUpperCase();
  return (
    <div className="mp-bloco">
      <span className="mp-rotulo">
        Produtos da meta (grupo e/ou classificação do HOS) — vazio = tudo
      </span>
      <input
        className="mp-busca"
        placeholder="Buscar (ex.: VITAMINAS)"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />
      <div className="mp-filtro-colunas">
        {(
          [
            ['grupos', 'Grupos', grupos],
            ['classificacoes', 'Classificações', classes],
          ] as const
        ).map(([campo, titulo, lista]) => (
          <div key={campo}>
            <b>{titulo}</b>
            <div className="mp-filtro-lista">
              {lista
                .filter((x) => !q || x.includes(q))
                .map((x) => (
                  <label key={x} className="mp-check">
                    <input
                      type="checkbox"
                      checked={sel[campo].includes(x)}
                      onChange={() => alternar(campo, x)}
                    />
                    {x}
                  </label>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Requisitos({
  catalogo,
  regra,
  aoMudar,
  podeDinheiro,
}: {
  catalogo: IndicadorDoCatalogo[];
  regra: RegraDaMeta;
  aoMudar: (r: RegraDaMeta['requisitos']) => void;
  podeDinheiro: boolean;
}) {
  const r = regra.requisitos;
  const trocar = (i: number, q: Requisito) =>
    aoMudar({ ...r, itens: r.itens.map((x, k) => (k === i ? q : x)) });
  return (
    <div className="mp-bloco">
      <span className="mp-rotulo">Requisitos para receber (opcional)</span>
      <p className="ac-nota">
        Ex.: o ticket só paga se a margem ficar ≥ 30%. Sem o requisito, a tela mostra "meta
        atingida, bônus bloqueado" com o motivo.
      </p>
      {r.itens.map((q, i) => (
        <div key={i} className="mp-linha">
          <select
            value={q.indicador}
            disabled={!podeDinheiro}
            onChange={(e) =>
              trocar(i, {
                ...q,
                indicador: e.target.value,
                rotulo: catalogo.find((c) => c.codigo === e.target.value)?.rotulo ?? e.target.value,
              })
            }
          >
            {catalogo.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.rotulo}
              </option>
            ))}
          </select>
          <select
            value={q.operador}
            disabled={!podeDinheiro}
            onChange={(e) => trocar(i, { ...q, operador: e.target.value as '>=' | '<=' })}
          >
            <option value=">=">no mínimo</option>
            <option value="<=">no máximo</option>
          </select>
          <input
            inputMode="decimal"
            value={q.valor}
            disabled={!podeDinheiro}
            onChange={(e) => trocar(i, { ...q, valor: dec(e.target.value) })}
            aria-label="Valor do requisito"
          />
          <button
            type="button"
            className="ac-link mp-perigo"
            disabled={!podeDinheiro}
            onClick={() => aoMudar({ ...r, itens: r.itens.filter((_, k) => k !== i) })}
          >
            Remover
          </button>
        </div>
      ))}
      <div className="mp-linha">
        <button
          type="button"
          className="ac-botao"
          disabled={!podeDinheiro || r.itens.length >= 10}
          onClick={() =>
            aoMudar({
              ...r,
              itens: [
                ...r.itens,
                { indicador: 'margem_pct', rotulo: 'Margem', operador: '>=', valor: '30' },
              ],
            })
          }
        >
          <Icone nome="plus" tamanho={13} traco={2} /> Requisito
        </button>
        {r.itens.length > 1 && (
          <select
            value={r.modo}
            disabled={!podeDinheiro}
            onChange={(e) => aoMudar({ ...r, modo: e.target.value as 'TODOS' | 'ALGUM' })}
          >
            <option value="TODOS">todos devem ser cumpridos</option>
            <option value="ALGUM">pelo menos um</option>
          </select>
        )}
      </div>
    </div>
  );
}

function Publico({
  opcoes,
  regra,
  aoMudar,
}: {
  opcoes: OpcoesDasMetas | null;
  regra: RegraDaMeta;
  aoMudar: (p: Partial<RegraDaMeta>) => void;
}) {
  const p = regra.publico;
  const alternar = (campo: 'contas' | 'excecoes', id: string) => {
    const s = new Set(p[campo]);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    aoMudar({ publico: { ...p, [campo]: [...s] } });
  };
  const base = (id: string) => regra.personalizadas[id]?.base ?? '';
  return (
    <div className="mp-bloco">
      <span className="mp-rotulo">Público</span>
      <div className="mp-opcoes">
        <Opcao
          marcado={p.tipo === 'TODOS'}
          aoMarcar={() => aoMudar({ publico: { ...p, tipo: 'TODOS' } })}
          titulo="Todos os balconistas"
          texto="Com exceções, se quiser."
        />
        <Opcao
          marcado={p.tipo === 'CONTAS'}
          aoMarcar={() => aoMudar({ publico: { ...p, tipo: 'CONTAS' } })}
          titulo="Funcionários selecionados"
          texto="Só quem você marcar."
        />
      </div>
      <div className="mp-pessoas">
        {opcoes?.pessoas.map((x) => (
          <div key={x.id} className="mp-pessoa">
            <label className="mp-check">
              <input
                type="checkbox"
                checked={p.tipo === 'TODOS' ? !p.excecoes.includes(x.id) : p.contas.includes(x.id)}
                onChange={() => alternar(p.tipo === 'TODOS' ? 'excecoes' : 'contas', x.id)}
              />
              {x.nome} <span className="ac-mono">{x.login}</span>
            </label>
            <input
              className="mp-personalizada"
              inputMode="decimal"
              placeholder="base própria"
              title="Meta personalizada: base só desta pessoa (ex.: vitamina R$ 2.000)"
              value={base(x.id)}
              onChange={(e) => {
                const v = dec(e.target.value);
                const outras = { ...regra.personalizadas };
                if (v) outras[x.id] = { ...outras[x.id], base: v };
                else delete outras[x.id];
                aoMudar({ personalizadas: outras });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultadoDaSimulacao({
  sim,
  ind,
}: {
  sim: Simulacao;
  ind: IndicadorDoCatalogo | undefined;
}) {
  const mes = `${sim.competencia.slice(5, 7)}/${sim.competencia.slice(0, 4)}`;
  if (sim.semHistorico)
    return (
      <p className="ac-nota">
        Sem histórico suficiente em {mes} para simular esta meta (ninguém tinha base ou vendas).
      </p>
    );
  return (
    <>
      <div className="mp-sim-numeros">
        <div>
          <span>Mês simulado</span>
          <b>{mes}</b>
        </div>
        <div>
          <span>Elegíveis</span>
          <b>
            {sim.elegiveis} de {sim.funcionarios}
          </b>
        </div>
        <div>
          <span>Bônus estimado</span>
          <b>{reais(sim.total)}</b>
        </div>
        <div>
          <span>Média por quem ganhou</span>
          <b>{reais(sim.media)}</b>
        </div>
        <div>
          <span>Máximo individual</span>
          <b>{reais(sim.maximo)}</b>
        </div>
      </div>
      <table className="ac-tabela mp-sim-tabela" data-sem-ordenar>
        <thead>
          <tr>
            <th>Balconista</th>
            <th className="ac-num">Base</th>
            <th className="ac-num">Resultado</th>
            <th>Nível</th>
            <th className="ac-num">Bônus</th>
          </tr>
        </thead>
        <tbody>
          {sim.porConta.map((x) => (
            <tr key={x.accountId}>
              <td>{x.nome}</td>
              <td className="ac-num">
                {valorDoIndicador(x.meta?.base, ind?.unidade ?? 'RS', ind?.casas ?? 2)}
              </td>
              <td className="ac-num">
                {valorDoIndicador(x.meta?.valor, ind?.unidade ?? 'RS', ind?.casas ?? 2)}
              </td>
              <td>
                {x.meta?.situacao === 'SEM_DADO'
                  ? 'sem venda'
                  : x.meta?.situacao === 'SEM_BASE'
                    ? 'sem base'
                    : x.meta?.situacao === 'PARCIAL'
                      ? `parcial (N${String(x.meta.avaliacao?.nivel ?? 0)})`
                      : `N${String(x.meta?.avaliacao?.nivel ?? 0)}`}
              </td>
              <td className="ac-num">{reais(x.meta?.avaliacao?.premio ?? '0')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
